/**
 * The parent-organization picker for the brand and manufacturer editors.
 *
 * Lists `parent_organizations` documents — not lookup values — and dual-writes
 * on selection: `parentOrgId` for the foreign key, plus a `parentOrg` spelling
 * older app versions already match on (see `legacySpellingFor`).
 *
 * Controlled on the PAIR, so the host holds both strings and decides how each is
 * persisted. A stored value that resolves to no single organization is shown for
 * what it is and left alone unless the curator picks or clears: opening and
 * saving an editor must never quietly rewrite who owns a brand.
 */
import { useState } from 'react';
import { EntityPicker, optionMatches, type EntityOption } from './EntityPicker';
import {
  orgDisplayName,
  parentOrgSelection,
  resolveParentOrgRef,
  resolvedOrg,
  type ParentOrgIndex,
} from './parentOrgResolve';

export interface ParentOrgValue {
  parentOrgId: string;
  parentOrg: string;
}

interface Props {
  label: string;
  value: ParentOrgValue;
  index: ParentOrgIndex;
  loading?: boolean;
  /** The collection failed to load — say so rather than flag the value unmatched. */
  error?: boolean;
  onChange: (next: ParentOrgValue) => void;
  disabled?: boolean;
}

export function ParentOrgPicker({
  label,
  value,
  index,
  loading = false,
  error = false,
  onChange,
  disabled = false,
}: Props) {
  const [query, setQuery] = useState('');
  const resolution = resolveParentOrgRef(value, index);
  const org = resolvedOrg(resolution);

  const options: EntityOption[] = index.sorted
    .map((o) => ({ id: o.id, label: orgDisplayName(o), sub: o.country }))
    .filter((o) => optionMatches(o, query));

  const clear = () => onChange(parentOrgSelection(null));

  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-gray-700">{label}</label>
      <EntityPicker
        value={org?.id ?? ''}
        selectedLabel={org ? orgDisplayName(org) : undefined}
        options={options}
        query={query}
        onQueryChange={setQuery}
        onSelect={(id) => {
          const picked = id ? index.byId.get(id) ?? null : null;
          onChange(parentOrgSelection(picked));
        }}
        ariaLabel={label}
        placeholder={loading ? 'Loading organizations…' : 'Search parent organizations…'}
        loading={loading}
        disabled={disabled}
        allowClear
      />

      {error && (
        <p className="mt-1 text-xs text-amber-700">
          Parent organizations could not be loaded — the stored value is kept as it is.
          {value.parentOrg && <> Currently “{value.parentOrg}”.</>}
        </p>
      )}

      {!error && !loading && resolution.kind === 'legacy' && !value.parentOrgId && (
        <p className="mt-1 text-xs text-gray-500">
          Matched from the stored name “{resolution.raw}”. Pick it again to link it by id.
        </p>
      )}

      {!error && !loading && resolution.kind !== 'none' && !org && (
        <p className="mt-1 text-xs text-amber-700">
          {resolution.kind === 'ambiguous' &&
            `Stored as “${resolution.raw}”, which ${resolution.candidates.length} organizations answer to — pick the right one.`}
          {resolution.kind === 'unmatched' &&
            `Stored as “${resolution.raw}”, which matches no parent organization — pick one to link it, or leave it as text.`}
          {resolution.kind === 'dangling' &&
            `Linked to “${resolution.id}”, which no longer exists — pick another organization.`}{' '}
          <button
            type="button"
            className="text-red-600 hover:underline disabled:text-gray-300"
            disabled={disabled}
            onClick={clear}
          >
            Clear
          </button>
        </p>
      )}
    </div>
  );
}
